import React, { useMemo } from "react";

// Radial semantic concept map rendered as scalable SVG.
export const ConceptMap = ({ concepts = [], title = "Topic", selectedName, onSelect, variant = "full" }) => {
  const W = 800;
  const H = 600;
  const cx = W / 2;
  const cy = H / 2;
  const isMini = variant === "mini";
  const R = isMini ? 170 : 235;
  const nodeR = isMini ? 30 : 46;

  const nodes = useMemo(() => {
    const n = Math.max(concepts.length, 1);
    return concepts.map((c, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      return { ...c, x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
    });
  }, [concepts, cx, cy, R]);

  const byName = useMemo(() => {
    const m = {};
    nodes.forEach((nd) => (m[nd.name.toLowerCase()] = nd));
    return m;
  }, [nodes]);

  const relEdges = useMemo(() => {
    const seen = new Set();
    const out = [];
    nodes.forEach((nd) => {
      (nd.related || []).forEach((r) => {
        const target = byName[(r || "").toLowerCase()];
        if (!target || target.name === nd.name) return;
        const key = [nd.name, target.name].sort().join("|");
        if (seen.has(key)) return;
        seen.add(key);
        out.push([nd, target]);
      });
    });
    return out;
  }, [nodes, byName]);

  const truncate = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" style={{ overflow: "visible" }} data-testid="concept-map">
      {/* center -> node spokes */}
      {nodes.map((nd) => (
        <line key={`spoke-${nd.name}`} x1={cx} y1={cy} x2={nd.x} y2={nd.y} stroke="rgba(0,229,255,0.14)" strokeWidth={1} />
      ))}
      {/* related edges */}
      {relEdges.map(([a, b], i) => (
        <line key={`rel-${i}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="rgba(0,229,255,0.28)" strokeWidth={1.4} strokeDasharray="4 4" />
      ))}

      {/* center node */}
      <g>
        <circle cx={cx} cy={cy} r={nodeR + 6} fill="rgba(0,229,255,0.12)" stroke="#00E5FF" strokeWidth={1.5} />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#F8FAFC" fontSize={isMini ? 13 : 16} fontFamily="Outfit" fontWeight="500">
          {truncate(title, isMini ? 12 : 16)}
        </text>
      </g>

      {/* concept nodes */}
      {nodes.map((nd) => {
        const active = selectedName && nd.name.toLowerCase() === selectedName.toLowerCase();
        return (
          <g
            key={nd.name}
            onClick={() => onSelect?.(nd)}
            style={{ cursor: onSelect ? "pointer" : "default" }}
            data-testid="concept-node"
          >
            <circle
              cx={nd.x}
              cy={nd.y}
              r={nodeR}
              fill={active ? "rgba(0,229,255,0.22)" : "#0F1A2A"}
              stroke="#00E5FF"
              strokeWidth={active ? 2.5 : 1.4}
              style={active ? { filter: "drop-shadow(0 0 10px rgba(0,229,255,0.6))" } : undefined}
            />
            <text
              x={nd.x}
              y={nd.y}
              textAnchor="middle"
              dominantBaseline="central"
              fill={active ? "#00E5FF" : "#CBD5E1"}
              fontSize={isMini ? 10 : 12}
              fontFamily="IBM Plex Sans"
            >
              {truncate(nd.name, isMini ? 10 : 14)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
